import { createBlockConfigResolver } from '../block-config-resolver';
import type { ResolveBlockConfigRuntimeInput } from '../block-config-resolver';
import {
  buildOverrideConfig,
  buildResolvedConfig,
  buildSharedAccessors,
  componentTypeToComposeConfigExportName,
  componentTypeToPrefix,
  createComposeConfigFn,
} from '../dood-block-config';
import type {
  ComposeConfigExportName,
  ComposeConfigFn,
  OverrideConfig,
  RequiredDescriptorControlValues,
  RequiredPropValues,
  ResolvedConfig,
  SharedFieldAccessors,
  SharedFieldsDefinition,
} from '../dood-block-config';

/* -------------------------------------------------------------------------- */
/* Atom metadata                                                              */
/* -------------------------------------------------------------------------- */

export type DoodAtomMetadata<
  SharedFields extends SharedFieldsDefinition,
  Name extends string = string,
> = {
  /** Human-readable atom name, e.g. `"Tab list"`. */
  name: Name;
  /** Exact Angular component class name, e.g. `TabListAtomComponent`. */
  componentType: string;
  sharedFields: SharedFields;
};

/* -------------------------------------------------------------------------- */
/* Definition result                                                          */
/* -------------------------------------------------------------------------- */

export type DoodAtomDefinition<Config = Record<string, unknown>> = {
  /** Raw config tree with `propertyValue` / `descriptorControlField` leaves. */
  properties: Config;
  /** Static config snapshot (conditions resolve to undefined without a context). */
  config: ResolvedConfig<Config>;
  configToOverride: OverrideConfig<Config>;
  /** Pure runtime merge + conditional resolution (framework-agnostic). */
  resolveConfig: (
    input: ResolveBlockConfigRuntimeInput<ResolvedConfig<Config>>,
  ) => ResolvedConfig<Config>;
};

export type PrefixedDoodAtomDefinition<
  Config,
  ComponentType extends string,
> = {
  [Key in `${ComponentTypePrefix<ComponentType>}Config`]: ResolvedConfig<Config>;
} & {
  [Key in ComposeConfigExportName<ComponentType>]: ComposeConfigFn<
    Config & Record<string, unknown>,
    {}
  >;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}Properties`]: Config;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}ResolveConfig`]: (
    input: ResolveBlockConfigRuntimeInput<ResolvedConfig<Config>>,
  ) => ResolvedConfig<Config>;
};

type ComponentTypePrefix<ComponentType extends string> =
  ComponentType extends `${infer Base}Component`
    ? Uncapitalize<Base>
    : Uncapitalize<ComponentType>;

/* -------------------------------------------------------------------------- */
/* doodAtomConfig                                                             */
/* -------------------------------------------------------------------------- */

export function doodAtomConfig<
  const SharedFields extends SharedFieldsDefinition,
  const Name extends string,
  const ComponentType extends string,
  const Config extends RequiredPropValues<SharedFields> &
    RequiredDescriptorControlValues<SharedFields>,
>(
  metadata: DoodAtomMetadata<SharedFields, Name> & { readonly componentType: ComponentType },
  configFactory: (
    shared: SharedFieldAccessors<SharedFields>,
  ) => Config,
): DoodAtomDefinition<Config> & PrefixedDoodAtomDefinition<Config, ComponentType> {
  const shared = buildSharedAccessors<SharedFields>(metadata.sharedFields);
  const config = configFactory(shared);
  const resolved = buildResolvedConfig(config);
  const override = buildOverrideConfig(config);
  const resolverDefinition = {
    properties: config,
    config: resolved,
    configToOverride: override as Partial<ResolvedConfig<Config>>,
  };
  const composeConfig = createComposeConfigFn(
    config as Record<string, unknown>,
  ) as ComposeConfigFn<Config & Record<string, unknown>, {}>;
  const definition = {
    properties: config,
    config: resolved,
    configToOverride: override,
    resolveConfig: createBlockConfigResolver(resolverDefinition),
  };
  return {
    ...definition,
    ...buildPrefixedAtomDefinition(metadata.componentType, definition, composeConfig),
  } as DoodAtomDefinition<Config> & PrefixedDoodAtomDefinition<Config, ComponentType>;
}

function buildPrefixedAtomDefinition<Config>(
  componentType: string,
  definition: DoodAtomDefinition<Config>,
  composeConfig: ComposeConfigFn<Config & Record<string, unknown>, {}>,
): Record<string, unknown> {
  const prefix = componentTypeToPrefix(componentType);
  return {
    [`${prefix}Config`]: definition.config,
    [componentTypeToComposeConfigExportName(componentType)]: composeConfig,
    [`${prefix}Properties`]: definition.properties,
    [`${prefix}ResolveConfig`]: definition.resolveConfig,
  };
}
